<template>
  <div class="main-product-root datagrid-root">
    <div class="content-block">
      <div class="headline-container">
        <div class="headline-item"><h2>Stamdata</h2></div>
        <div class="subheadline-item">&nbsp; - Varer</div>
      </div>
    </div>

    <div class="content-block">
      <div class="supplier-container">
        Supplier:
        <dx-select-box
          :data-source="supplierList.data"
          display-expr="name"
          value-expr="id"
          v-model:value="supplierList.selectedSupplier"
          @value-changed="supplierChanged" />
      </div>
      <div class="grid-button-container">
        <div class="flex-align-right">
          <dx-button @click="clearFilterAndSorting" icon="clearfilter" hint="Fjern filter og sortering"></dx-button>
        </div>
      </div>
      <dx-data-grid
        ref="dataGrid"
        id="mainProductDataGrid"
        :data-source="datasource"
        height="75vh"
        :focused-row-key="focusedRowKey"
        @init-new-row="initNewRow"
        @row-removing="rowRemoving">

        <dx-selection mode="single"/>
        <dx-export :enabled="true" fileName="Varer" />
        <dx-filter-row :visible="true" />
        <dx-header-filter :visible="false" />
        <dx-grouping :auto-expand-all="false" />
        <dx-group-panel :visible="false" />
        <dx-state-storing :enabled="true" type="sessionStorage" storage-key="mainProduct-list" />
        <dx-scrolling mode="virtual" />
        <dx-paging :page-size="50" />

        <dx-editing
          :allow-updating="true"
          :allow-deleting="true"
          :allow-adding="true"
          :confirm-delete="false"
          mode="form">
          <dx-form
            :col-count="1"
            label-location="top"
            :showValidationSummary="true">
            <dx-simple-item data-field="supplierId">
              <dx-label text="Leverandør" />
            </dx-simple-item>
            <dx-simple-item data-field="identifier">
              <dx-label text="Varenummer" />
            </dx-simple-item>
            <dx-simple-item data-field="name">
              <dx-label text="Name" />
            </dx-simple-item>
            <dx-simple-item data-field="brands">
              <dx-label text="Brands" />
            </dx-simple-item>
            <dx-simple-item data-field="active" :editor-options="activeEditorOptions">
              <dx-label :visible="false" />
            </dx-simple-item>
          </dx-form>
        </dx-editing>

        <dx-column
          data-field="name"
          caption="Navn"
          data-type="string"
          sort-order="asc"
          :hiding-priority="8">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="identifier"
          caption="Varenummer"
          data-type="string"
          :hiding-priority="7"
          :width="130">
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="supplierId"
          caption="Leverandør"
          :hiding-priority="6"
          :visible="false"
          data-type="number"
          :form-item="supplierFormItem"
          :allow-editing="false">
          <dx-grid-lookup
            :data-source="supplierList.data"
            value-expr="id"
            display-expr="name" />
          <dx-required-rule />
        </dx-column>

        <dx-column
          data-field="active"
          caption="Aktiv"
          data-type="boolean"
          :hiding-priority="5"
          :width="130" />

        <dx-column
          data-field="brands"
          :visible="false"
          caption="Brands"
          data-type="object"
          edit-cell-template="brandSelector">
          <dx-required-rule />
        </dx-column>

        <dx-column
         type="buttons"
         :visible="false" />

        <dx-master-detail
          :enabled="false"
          template="detailTemplate" />

        <template #detailTemplate="{ data: row }">
          <ContentLoaderList v-if="!loadingDataCompleted" />
          <div v-else>
            <DetailView
              :row="row"
              :supplierList="supplierList.data"
              :brandList="brandList.data"
              @onEditClick="onEditClick"
              @onDeleteClick="onDeleteClick" />
          </div>
        </template>

        <template #brandSelector="{ data }">
          <div>
            <dx-tag-box
              :items="brandList.data"
              value-expr="id"
              display-expr="name"
              :show-selection-controls="true"
              :search-enabled="true"
              :value="data.value"
              @value-changed="data.setValue($event.value)" />
          </div>
        </template>
      </dx-data-grid>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import DetailView from '@/modules/masterData/mainProducts/components/detail-view.vue';
import ContentLoaderList from '@/components/skeletonLoaders/content-loader-list.vue';
import BaseDataGrid from '@/components/common/base-datagrid.vue';
import useDatagrid from '@/modules/masterData/mainProducts/composables/datagrid.composable.js';
import useMasterdata from '@/modules/masterData/mainProducts/composables/masterdata.composable.js';

const activeEditorOptions = {
  text: 'Aktiv',
};

const supplierFormItem = {
  visible: false,
};

export default {
  name: 'MainProductModule',
  extends: BaseDataGrid,
  components: {
    DetailView,
    ContentLoaderList,
  },
  setup() {
    const {
      datasource,
      getDatasource,
      onInitNewRow,
    } = useDatagrid();

    const {
      supplierList,
      brandList,
      loadBrandList,
      saveSelectedSupplierInStore,
    } = useMasterdata();

    const loadingDataCompleted = computed(() => supplierList.loadingCompleted && brandList.loadingCompleted);

    const supplierChanged = () => {
      const supplierId = supplierList.selectedSupplier;

      saveSelectedSupplierInStore(supplierId);
      getDatasource(supplierId);
      loadBrandList(supplierId);
    };

    return {
      supplierList,
      brandList,
      loadingDataCompleted,
      datasource,
      activeEditorOptions,
      supplierChanged,
      supplierFormItem,
      onInitNewRow,
    };
  },
  methods: {
    initNewRow(e) {
      this.onInitNewRow(e, this.supplierList.selectedSupplier);
    },
    rowRemoving(e) {
      this.onRowRemoving(e, `Vil du slette denne vare?<br/><b>${e.data.name}</b>`, 'Slet vare');
    },
  },
};
</script>

<style lang="scss">
.main-product-root {
  .headline-container {
    display: flex;

    .headline-item {
      flex: 0 0 110px;

      h2 {
        margin-top: 0px;
      }
    }

    .subheadline-item {
      padding-top: 11px; font-size: 20px;
    }
  }

  .link:hover {
    cursor: pointer;
  }

  .supplier-container {
    padding: 20px;
    background-color: #FFF;
  }
}
</style>
